
import type { Column, SortDirection } from '../claim-history/simple-table'
import type { Patient, QueryParams } from './advanced-search-hook'
import type { LegacyRef } from 'react'

import { keepPreviousData } from '@tanstack/react-query'
import cx from 'classnames'
import _ from 'lodash'
import { useMemo, useState } from 'react'
import { BsExclamationTriangleFill, BsFillTelephoneFill } from 'react-icons/bs'
import { MdHome, MdMail, MdPhone, MdPhoneDisabled, MdSmartphone, MdClear } from 'react-icons/md'
import { RiFolderTransferFill } from 'react-icons/ri'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useDebounce, useMeasure } from 'react-use'
// @ts-expect-error // it's okay
import statuses from 'shared_src/dictionaries/episodeOfCare/statuses'
// @ts-expect-error // it's okay
import PartnerOrganizations from 'shared_src/dictionaries/partnerOrganizations'

import { getOnlyAvailableUserClients } from '../../selectors/user'
import { useAppSelector } from '../../store'
import * as Table from '../claim-history/simple-table'
import * as Card from '../shared/card'
import { FriendlyDateTime } from '../shared/Date/friendly-date-time'
import FriendlyDate from '../shared/Date/FriendlyDate'
import { Divider } from '../shared/Divider'
import * as Grid from '../shared/grid'
import FormattedPhone from '../shared/Phone/FormattedPhone'

import { StatusesEnum, useAdvancedSearchHook } from './advanced-search-hook'
import styles from './advanced-search.module.css'
import * as AdvancedSearchComponents from './components'

// Please enter search criteria

const scopes = [
  {
    label: 'All',
    value: 'all'
  },
  {
    label: 'My Patients',
    value: 'mine'
  },
  {
    label: 'My Team',
    value: 'team'
  }
]

const managingOrg: Array<{ label: string, value: string }> = [{ value: 'all', label: 'All' }, { value: 'null', label: 'None' }, ...PartnerOrganizations ]

export function AdvancedSearch (): JSX.Element {
  const navigate = useNavigate()
  const [ urlSearchParams, setURLSearchParams ] = useSearchParams()
  const [ measureRef, { width }] = useMeasure()
  const [ value, setValue ] = useState<string>(urlSearchParams.get('anyName') ?? '')
  const clients = useAppSelector((state) => getOnlyAvailableUserClients(state))
  const allClients = useAppSelector((redux) => redux?.globalVars?.clients ?? [])

  const isMediumView = width < 970 && width >= 700
  const isSmallView = width < 700 && width >= 500
  const isMobileView = width < 500

  let normalizedState: QueryParams = useMemo(() => ({
    anyName: urlSearchParams.get('anyName') ?? '',
    scope: urlSearchParams.get('scope') ?? 'all',
    status: urlSearchParams.get('status') as StatusesEnum ?? StatusesEnum.all,
    count: urlSearchParams.get('count') ? Number(urlSearchParams.get('count')) : 50,
    page: urlSearchParams.get('page') ? Number(urlSearchParams.get('page')) : 0,
    clientName: urlSearchParams.get('clientName') ?? 'all',
    sortBy: urlSearchParams.get('sortBy') ?? 'lastName',
    sortDir: urlSearchParams.get('sortDir') ?? 'asc',
    managingOrg: urlSearchParams.get('managingOrg') ?? 'all',
    patientId: urlSearchParams.get('patientId') ?? undefined,
    memberId: urlSearchParams.get('memberId') ?? undefined,
    phone: urlSearchParams.get('phone') ?? undefined,
    ssn: urlSearchParams.get('ssn') ?? undefined,
    birthDate: urlSearchParams.get('birthDate') ?? undefined,
    opensearch: true
  }), [ urlSearchParams ])

  const [ query, reset, enabled ] = useAdvancedSearchHook(normalizedState, {
    placeholderData: keepPreviousData,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false
  })

  useDebounce(() => {
    setURLSearchParams((sp) => {
      sp.set('anyName', value ?? '')
      sp.set('page', '0')
      return sp
    })
  }, 501, [ value ])

  const clientsOptions: Array<{ label: string, value: string }> = [{ value: 'all', label: 'All' }, ...clients.map((client: any) => {
    return {
      label: client.display,
      value: client.clientName
    }
  })?.sort((a: any, b: any) => (a?.label < b?.label) ? -1 : ((a?.label > b?.label) ? 1 : 0)) ]

  const columns = useMemo((): Array<Column<Patient>> => [
    {
      name: 'lastName',
      header: 'Last',
      getValue: (item) => {
        const client = (item._clientName || item.clientName) as string
        return (
          <Link to={`/patient/${client}/${item.id}/patientFolder`}>{item.lastName}</Link>
        )
      },
      isSortable: true
    }, {
      name: 'firstName',
      header: 'First',
      isSortable: true
    }, {
      name: 'birthDate',
      header: 'DOB',
      isSortable: true,
      getValue: (item) => <FriendlyDate date={item.birthDate || ''} />
    }, {
      name: 'diagnosis',
      header: 'Program'
    }, {
      name: 'accuity',
      header: 'PAR',
      gridArea: '0.4fr',
      isSortable: true
    }, {
      name: 'ssn',
      header: 'SSN',
      gridArea: '1.2fr'
    },
    {
      name: 'phone',
      header: 'Phone',
      getValue: (item) => (
        item.phone || []).map((p) =>
        (
          <FormattedPhone
            key={p}
            number={p}
          />
        ))
    },
    {
      name: 'phones',
      gridArea: '0.5fr',
      header: (
        <span
          title='Lexis-Nexis phones on the chart'
          key='phone-missed-icon'
        >
          <MdPhone
            fill='#8A8A8A'
            size='1.1rem'
          />
        </span>
      ),
      getValue: (item) => {
        const { _clientName, clientName, id, other, openEncounterStatus } = item
        const client = (_clientName || clientName) as string

        function Indicator (): JSX.Element {
          return (
            _.isEmpty(other)
              ? (
                <div title='No Lexis-Nexis numbers on the chart'>
                  <MdPhoneDisabled
                    fill='#F2545A'
                    size='1.1rem'
                  />
                </div>
              )
              : (
                <div title='Lexis-Nexis numbers on the chart'>
                  <MdPhone
                    fill='#82B43C'
                    size='1.1rem'
                  />
                </div>
              )
          )
        }

        if (openEncounterStatus) {
          return (
            <Link
              to={`/patient/${client}/${id}/assessment/standarddemographicspatient`}
            >
              <Indicator />
            </Link>
          )
        }
        return <Indicator />
      }
    },
    {
      name: 'continueEncounter',
      gridArea: '0.5fr',
      header: (
        <span
          title='Continue open encounter'
          key='play-square-icon'
        >
          <RiFolderTransferFill
            fill='#8A8A8A'
            size='1.1rem'
          />
        </span>
      ),
      getValue: (item) => {
        const client = (item._clientName || item.clientName) as string

        if (!item.openEncounterStatus) {
          return null
        }

        return (
          <Link to={`/patient/${client}/${item.id}/assessment/igao`}>
            <div title='Continue open encounter'>
              <RiFolderTransferFill
                fill='#967DD9'
                size='1.1rem'
              />
            </div>
          </Link>
        )
      }
    },
    {
      name: 'urgent',
      gridArea: '0.5fr',
      header: (
        <span
          title='Urgent actions on the chart'
          key='alert-triangle-icon'
        >
          <BsExclamationTriangleFill
            fill='#8A8A8A'
            size='1.1rem'
          />
        </span>
      ),
      getValue: (item) => {
        if (!item.urgent) {
          return null
        }
        return (
          <div title='Urgent actions on the chart'>
            <BsExclamationTriangleFill
              fill='#F2545A'
              size='1.1rem'
            />
          </div>
        )
      }
    },
    {
      name: 'lastEncounter',
      header: 'Last Encounter',
      gridArea: '1.2fr',
      getValue: (item) => (
        <FriendlyDateTime
          full
          datetime={item.lastEncounter || ''}
        />
      ),
      isSortable: true
    },
    {
      name: 'lastType',
      isSortable: true,
      header: 'Type',
      getValue: (item) => {
        if (item.lastType === 'home') {
          return (
            <div title='Home Encounter'>
              <MdHome fill='#82B43C' />
            </div>
          )
        }
        if (item.lastType === 'phone') {
          return (
            <div title='Telephonic Encounter'>
              <BsFillTelephoneFill fill='#82B43C' />
            </div>
          )
        }

        if (item.lastType === 'email') {
          return (
            <div title='Email Encounter'>
              <MdMail fill='#82B43C' />
            </div>
          )
        }
        if (item.lastType === 'mobile') {
          return (
            <div title='Mobile Encounter'>
              <MdSmartphone fill='#82B43C' />
            </div>
          )
        }
      }
    },
    {
      name: 'status',
      header: 'Status',
      getValue: (item) => item.status
        ? _.find(statuses, x => x.value === item.status).label
        : 'Unknown',
      isSortable: true
    },
    {
      name: 'clientName',
      isSortable: true,
      header: 'Client',
      getValue: (item) => {
        const clientNames = _.reduce(
          allClients,
          (acc, c) => {
            return { ...acc, [c.clientName]: c.display }
          },
          {}
        )

        const client = item._clientName || item.clientName
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        return clientNames[client as string] || client
      }
    }
  ], [ allClients ])

  const pagination = {
    page: urlSearchParams.get('page') ? Number(urlSearchParams.get('page')) : 0,
    pageSize: urlSearchParams.get('count') ? Number(urlSearchParams.get('count')) : 50,
    totalFiltered: query.data?.total
  }

  return (
    <AdvancedSearchComponents.Layout>
      <Card.Card>
        <Card.Header>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              flex: 1
            }}
          >
            <h2>Patient Search</h2>
            <Grid.Button
              onClick={() => {
                navigate('/new-patient')
              }}
              rounded
              style={{ border: '1px solid #4D1EE6' }}
            >
              Add Patient
            </Grid.Button>
          </div>
        </Card.Header>
        <AdvancedSearchComponents.Card>
          <div
            ref={measureRef as LegacyRef<HTMLDivElement>}
            className={cx(styles.searchContainer, {
              [styles.medium as string]: isMediumView,
              [styles.small as string]: isSmallView,
              [styles.mobile as string]: isMobileView
            })}
          >
            <div className={styles.searchInputContainer}>
              <div style={{ flex: 4 }}>
                <Grid.Input
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  isClearable
                  type='text'
                  autoComplete='off'
                  onChange={value => setValue(value)}
                  value={value ?? ''}
                  placeholder='Search patient by name, dob, ssn, phone, patient id, member id, client'
                />
              </div>
              <div className={styles.divider} />
              <AdvancedSearchComponents.Select
                options={statuses}
                value={statuses.find((st: { label: string, value: string }) => st.value === (urlSearchParams.get('status') ?? 'all'))}
                placeholder='Patient Status'
                onChange={(item) => setURLSearchParams((sp) => {
                  sp.set('status', item?.value ?? 'all')
                  sp.set('page', '0')
                  return sp
                })}
                isClearable={isMobileView}
                controlStyles={!isMobileView ? { border: 'none' } : {}}
                indicatorSeparator={!isMobileView ? { display: 'none' } : {}}
              />
            </div>
            <AdvancedSearchComponents.Select
              options={clientsOptions}
              value={clientsOptions.find((co) => co.value === (urlSearchParams.get('clientName') ?? 'all'))}
              isClearable
              placeholder='Client Name'
              onChange={(item) => {
                setURLSearchParams((sp) => {
                  sp.set('clientName', item?.value ?? 'all')
                  sp.set('page', '0')
                  return sp
                })
              }}
            />
            <AdvancedSearchComponents.Select
              options={managingOrg}
              value={managingOrg.find((po) => po.value === (urlSearchParams.get('managingOrg') ?? 'all'))}
              isClearable
              placeholder='Managing Organization'
              onChange={(item) => {
                setURLSearchParams((sp) => {
                  sp.set('managingOrg', item?.value ?? 'all')
                  sp.set('page', '0')
                  return sp
                })
              }}
            />
            <AdvancedSearchComponents.Select
              options={scopes}
              value={scopes.find((scope: { label: string, value: string }) => scope.value === (urlSearchParams.get('scope') ?? 'all'))}
              isClearable
              placeholder='Type of Patients'
              onChange={(item) => {
                setURLSearchParams((sp) => {
                  sp.set('scope', item?.value ?? 'all')
                  sp.set('page', '0')
                  return sp
                })
              }}
            />
            <button
              title='Clear filters'
              onClick={() => {
                reset()
                setValue('')
              }}
              className={styles.clearButton}
            >
              <MdClear size='1.7rem' />
            </button>
          </div>
        </AdvancedSearchComponents.Card>

        <Divider />

        <Table.Table
          expandable={false}
          data={enabled ? query.data?.data ?? [] : []}
          error={query.error as Error}
          fetchStatus={query.fetchStatus}
          queryStatus='success'
          noDataPlaceholder={enabled ? 'No results match the search and filter criteria' : 'Please enter search criteria'}
          sort={{ field: (urlSearchParams.get('sortBy') ?? 'lastName'), direction: (urlSearchParams.get('sortDir') ?? 'asc') as SortDirection }}
          onSortChange={(field, dir) => {
            setURLSearchParams((sp) => {
              sp.set('sortBy', field as string)
              sp.set('sortDir', dir)
              return sp
            })
          }}
          columns={columns}
          pagination={pagination}
          onPageSizeChange={(size) => {
            setURLSearchParams((sp) => {
              sp.set('count', String(size ?? 50))
              sp.set('page', '0')
              return sp
            })
          }}
          onPageChange={(page) => {
            setURLSearchParams((sp) => {
              sp.set('page', String(page - 1))
              return sp
            })
          }}
          disableMinHeight
        />
      </Card.Card>
    </AdvancedSearchComponents.Layout>

  )
}
